import { Lock, Mail, Phone, User, Users } from "lucide-react";
import { Button, Col, Container, Form, InputGroup, Row, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { isValidBdPhone, register as registerUser } from "../../../services/auth.api";

/**
 * Registration is phone-first: the number is the login identity and the only
 * channel the verification code goes to. E-mail is an optional profile detail,
 * and "reference" is another customer's phone number, not a free-text code.
 */
const RegisterForm = ({ onRequireVerification }) => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "all",
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
      referencePhone: "",
      agreeTerms: true,
    },
  });

  const onSubmit = async (data) => {
    try {
      await registerUser({
        name: data.fullName,
        phone: data.phone,
        password: data.password,
        email: data.email || undefined,
        referencePhone: data.referencePhone || undefined,
      });
      onRequireVerification(data.phone);
      navigate("/authentication/verify");
    } catch (err) {
      toast.error(err?.message || "Registration failed. Please try again.");
    }
  };

  return (
    <Container className="p-1 p-lg-4" style={{ maxWidth: "42rem" }}>
      <h2 className="text-center fw-bold text-dark mb-4">SIGN UP NOW</h2>

      <Form className="d-flex flex-column gap-2" onSubmit={handleSubmit(onSubmit)}>
        {/* Full Name */}
        <InputGroup>
          <InputGroup.Text
            className={`bg-white border-end-0 rounded-start-pill ps-4 ${errors.fullName ? "border-danger" : ""}`}
          >
            <User size={20} className="text-secondary" />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Full Name"
            {...register("fullName", {
              required: "Full Name is required",
              minLength: { value: 2, message: "Name must be at least 2 characters" },
            })}
            className={`border-start-0 rounded-end-pill py-2 shadow-none ${errors.fullName ? "border-danger" : ""}`}
          />
        </InputGroup>
        {errors.fullName && (
          <span className="text-danger small ms-2">{errors.fullName.message}</span>
        )}

        {/* Phone + Email */}
        <Row className="g-2">
          <Col xs={12}>
            <InputGroup>
              <InputGroup.Text
                className={`bg-white border-end-0 rounded-start-pill ps-4 ${errors.phone ? "border-danger" : ""}`}
              >
                <Phone size={20} className="text-secondary" />
              </InputGroup.Text>
              <Form.Control
                type="tel"
                placeholder="01712345678"
                {...register("phone", {
                  required: "Phone number is required",
                  validate: (value) =>
                    isValidBdPhone(value) ||
                    "Enter a valid Bangladeshi phone number (e.g. 01712345678)",
                })}
                className={`border-start-0 rounded-end-pill py-2 shadow-none ${errors.phone ? "border-danger" : ""}`}
              />
            </InputGroup>
            {errors.phone && (
              <span className="text-danger small ms-2">{errors.phone.message}</span>
            )}
          </Col>

          <Col xs={12}>
            <InputGroup>
              <InputGroup.Text
                className={`bg-white border-end-0 rounded-start-pill ps-4 ${errors.email ? "border-danger" : ""}`}
              >
                <Mail size={20} className="text-secondary" />
              </InputGroup.Text>
              <Form.Control
                type="email"
                placeholder="Email (optional)"
                {...register("email", {
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Invalid email address",
                  },
                })}
                className={`border-start-0 rounded-end-pill py-2 shadow-none ${errors.email ? "border-danger" : ""}`}
              />
            </InputGroup>
            {errors.email && (
              <span className="text-danger small ms-2">{errors.email.message}</span>
            )}
          </Col>
        </Row>

        {/* Passwords */}
        <Row className="g-3">
          <Col xs={12} md={6}>
            <InputGroup>
              <InputGroup.Text
                className={`bg-white border-end-0 rounded-start-pill ps-4 ${errors.password ? "border-danger" : ""}`}
              >
                <Lock size={20} className="text-secondary" />
              </InputGroup.Text>
              <Form.Control
                type="password"
                placeholder="Password"
                {...register("password", {
                  required: "Password is required",
                  minLength: { value: 8, message: "Password must be at least 8 characters" },
                  maxLength: { value: 72, message: "Password must be at most 72 characters" },
                })}
                className={`border-start-0 rounded-end-pill py-2 shadow-none ${errors.password ? "border-danger" : ""}`}
              />
            </InputGroup>
            {errors.password && (
              <span className="text-danger small ms-2">{errors.password.message}</span>
            )}
          </Col>

          <Col xs={12} md={6}>
            <InputGroup>
              <InputGroup.Text
                className={`bg-white border-end-0 rounded-start-pill ps-4 ${errors.confirmPassword ? "border-danger" : ""}`}
              >
                <Lock size={20} className="text-secondary" />
              </InputGroup.Text>
              <Form.Control
                type="password"
                placeholder="Confirm Password"
                {...register("confirmPassword", {
                  required: "Confirm Password is required",
                  validate: (value) => value === watch("password") || "Passwords do not match",
                })}
                className={`border-start-0 rounded-end-pill py-2 shadow-none ${errors.confirmPassword ? "border-danger" : ""}`}
              />
            </InputGroup>
            {errors.confirmPassword && (
              <span className="text-danger small ms-2">{errors.confirmPassword.message}</span>
            )}
          </Col>
        </Row>

        {/* Reference — the phone number of the customer who referred you */}
        <InputGroup className="mb-2">
          <InputGroup.Text className="bg-white border-end-0 rounded-start-pill ps-4">
            <Users size={20} className="text-secondary" />
          </InputGroup.Text>
          <Form.Control
            type="tel"
            placeholder="Reference phone number (optional)"
            {...register("referencePhone", {
              validate: (value) =>
                !value ||
                isValidBdPhone(value) ||
                "Enter a valid Bangladeshi phone number",
            })}
            className={`border-start-0 rounded-end-pill py-2 shadow-none ${errors.referencePhone ? "border-danger" : ""}`}
          />
        </InputGroup>
        {errors.referencePhone && (
          <span className="text-danger small ms-2">{errors.referencePhone.message}</span>
        )}

        {/* Terms */}
        <Form.Check
          type="checkbox"
          id="terms-check"
          label="I confirm that all the above information I have provided is correct"
          {...register("agreeTerms", { required: "You must agree to the terms" })}
          className="text-secondary small mb-1"
        />
        {errors.agreeTerms && (
          <span className="text-danger small ms-2">{errors.agreeTerms.message}</span>
        )}

        <Button
          type="submit"
          variant="primary"
          disabled={isSubmitting}
          className="w-100 rounded-start-pill py-2 fw-semibold d-flex align-items-center justify-content-center gap-2"
          style={{ backgroundColor: "#1B6DC1", borderColor: "#1B6DC1" }}
        >
          Sign Up
          {isSubmitting && <Spinner animation="border" size="sm" />}
        </Button>

        <div className="text-center text-secondary">
          Already have an account?{" "}
          <Link
            to="/authentication/login"
            className="p-0 fw-semibold text-decoration-none"
            style={{ color: "#1B6DC1", fontSize: "1rem" }}
          >
            Sign In
          </Link>
        </div>
      </Form>
    </Container>
  );
};

export default RegisterForm;
